import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';

const CurrentPlanCard = ({ plan, onUpgrade, onCancel, cancelLoading }) => {
  const { t } = useTranslation();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const isPaidPlan = Boolean(plan?.planId) && !plan?.isTrial && plan?.statusKey !== 'trial_ended' && !plan?.noPlanSelected;
  const isActive = plan?.statusKey === 'active' || plan?.statusKey === 'over_limit';
  const canCancel = isPaidPlan && isActive && !plan?.cancelAtPeriodEnd;

  const getStatusColor = (statusKey) => {
    switch (statusKey) {
      case 'active':
        return 'text-success bg-success/10';
      case 'select_a_plan':
        return 'text-warning bg-warning/10';
      case 'expired':
      case 'trial_ended':
      case 'canceled':
      case 'canceling':
        return 'text-error bg-error/10';
      case 'past_due':
        return 'text-error bg-error/10';
      case 'over_limit':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getPlanIcon = () => {
    if (plan?.isTrial || plan?.statusKey === 'trial_ended') return 'Gift';
    const id = plan?.planId || '';
    if (id.includes('pro')) return 'Trophy';
    if (id.includes('essential')) return 'Award';
    return 'Package';
  };

  const title = plan?.noPlanSelected
    ? plan?.typeLabel
    : plan?.isTrial || plan?.statusKey === 'trial_ended'
      ? (plan?.statusKey === 'trial_ended'
        ? plan?.typeLabel
        : t('billing.planSuffix', { type: plan?.typeLabel }))
      : plan?.typeLabel;

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-md">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 md:gap-6">
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-lg bg-primary/10">
            <Icon name={getPlanIcon()} size={24} color="var(--color-primary)" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h2 className={`text-xl md:text-2xl lg:text-3xl font-semibold ${plan?.statusKey === 'trial_ended' ? 'text-error' : 'text-foreground'}`}>
                {title}
              </h2>
              {plan?.statusKey !== 'trial_ended' && (
                <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium ${getStatusColor(plan?.statusKey)}`}>
                  {plan?.status}
                </span>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
                {plan?.noPlanSelected ? (
                  <span className="text-base md:text-lg font-normal text-muted-foreground">
                    {t('billing.chooseBelow')}
                  </span>
                ) : plan?.isTrial ? (
                  <>
                    €0
                    <span className="text-sm md:text-base lg:text-lg font-normal text-muted-foreground"> {t('billing.trial')}</span>
                  </>
                ) : (
                  <>
                    €{plan?.price}
                    <span className="text-sm md:text-base lg:text-lg font-normal text-muted-foreground">{t('billing.perMonth')}</span>
                  </>
                )}
              </p>
              {!plan?.noPlanSelected && (
                <p className="text-sm md:text-base text-muted-foreground">
                  {plan?.cancelAtPeriodEnd
                    ? `${t('billing.cancelsOn')} ${plan?.nextPaymentDate}`
                    : plan?.isTrial
                      ? `${t('billing.trialEnds')} ${plan?.nextPaymentDate}`
                      : `${t('billing.nextPayment')} ${plan?.nextPaymentDate}`}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
          <Button
            variant="default"
            size="lg"
            iconName="ArrowUpCircle"
            iconPosition="left"
            onClick={onUpgrade}
            fullWidth
            className="sm:w-auto"
          >
            {plan?.noPlanSelected ? t('billing.choosePlan') : t('billing.upgradePlan')}
          </Button>
          {canCancel && (
            <Button
              variant="outline"
              size="lg"
              iconName="XCircle"
              iconPosition="left"
              onClick={() => setShowCancelConfirm(true)}
              fullWidth
              className="sm:w-auto border-destructive/50 text-destructive hover:!bg-destructive hover:!text-destructive-foreground hover:!border-destructive"
              disabled={cancelLoading}
            >
              {cancelLoading ? t('billing.cancelling') : t('billing.cancelSubscription')}
            </Button>
          )}
        </div>
        <ConfirmDialog
          isOpen={showCancelConfirm}
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={async () => {
            await onCancel?.();
            setShowCancelConfirm(false);
          }}
          title={t('billing.cancelSubscription')}
          message={t('billing.cancelDialogMessage', { date: plan?.nextPaymentDate })}
          confirmText={t('billing.cancelSubscription')}
          cancelText={t('billing.keepSubscription')}
        />
      </div>
    </div>
  );
};

export default CurrentPlanCard;
